/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import {
    ComboBox,
    IComboBoxOption,
    ICommandBarItemProps,
    IToggleProps,
    IconButton,
    SelectableOptionMenuItemType,
    Stack,
    Toggle
} from '@fluentui/react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';
import { Intent } from 'src/luis/modules/intents/Intent';
import { Entity } from 'src/luis/projects/entities/types/Entity';
import { EntityRole } from 'src/luis/projects/entities/types/EntityRole';
import { LuisId } from 'src/luis/types/Resource';

export type SuggestUtterancesFilter = {
    /**
     * The name of the intent, entity, or role to filter by.
     */
    filterName: string;

    /**
     * The id of the intent, entity, or role to filter by.
     */
    filterId: LuisId;

    /**
     * The type of the filter (intent, entity, or role).
     */
    filterType: 'entities' | 'intents' | 'roles';
};

export type SuggestUtterancesToolbarProps = {
    /**
     * List of entities.
     */
    entities: Entity[];

    /**
     * List of all intents.
     */
    intents: Intent[];

    /**
     * List of all roles.
     */
    roles: EntityRole[];

    /**
     * Current selected filter.
     */
    currentFilter: SuggestUtterancesFilter;

    /**
     * Callback on changing the filter used.
     */
    onChangeFilter: (filter: SuggestUtterancesFilter) => void;

    /**
     * Callback to refresh the results.
     */
    onRefresh: () => void;
};

const messages = defineMessages({
    filter: {
        id: 'utterances.suggestTableToolbar.filter',
        defaultMessage: 'Filter:'
    },
    filterPlaceholder: {
        id: 'utterances.suggestTableToolbar.filterPlaceholder',
        defaultMessage: 'Choose intent/entity'
    },
    entitiesView: {
        id: 'utterances.suggestTableToolbar.entitiesView',
        defaultMessage: 'Entities view'
    },
    tokensView: {
        id: 'utterances.suggestTableToolbar.tokensView',
        defaultMessage: 'Tokens view'
    },
    refreshAriaLabel: {
        id: 'utterances.suggestTableToolbar.refreshAriaLabel',
        defaultMessage: 'Refresh utterances'
    }
});

const { styleComponent } = getStylist('SuggestTableToolbar');
export const ToggleView = styleComponent<IToggleProps>(Toggle)('ToggleView', theme => ({
    marginTop: theme.marginTop,
    marginLeft: theme.marginLeft
}));

/**
 * The component representing the command bar of the Utterances table in ReviewEndpointUtterances page
 */
export const SuggestUtterancesToolbar = (props: SuggestUtterancesToolbarProps) => {
    const { intents, entities, roles, currentFilter, onChangeFilter, onRefresh } = props;
    const { localize } = useLocalization();

    const mappedIntents: IComboBoxOption[] = intents.map(intent => ({ key: intent.id, text: intent.name }));
    const mappedEntities: IComboBoxOption[] = entities.map(entity => ({ key: entity.id, text: entity.name }));
    const mappedRoles: IComboBoxOption[] = roles.map(role => ({ key: role.id, text: role.name }));
    let filterList: IComboBoxOption[] = [];

    if (mappedIntents.length) {
        filterList = filterList.concat([
            { key: 'intents', text: 'Intents', itemType: SelectableOptionMenuItemType.Header },
            ...mappedIntents
        ]);
    }

    if (mappedEntities.length) {
        filterList = filterList.concat([
            { key: 'entities', text: 'Entities', itemType: SelectableOptionMenuItemType.Header },
            ...mappedEntities
        ]);
    }

    if (mappedRoles.length) {
        filterList = filterList.concat([{ key: 'roles', text: 'Roles', itemType: SelectableOptionMenuItemType.Header }, ...mappedRoles]);
    }

    const onFilterChange = (item: IComboBoxOption, index: number) => {
        let filterType: SuggestUtterancesFilter['filterType'];
        if (index <= intents.length) {
            filterType = 'intents';
        } else if (index <= intents.length + entities.length + 1) {
            filterType = 'entities';
        } else {
            filterType = 'roles';
        }
        onChangeFilter({ filterId: item.key, filterName: item.text, filterType });
    };

    const items: ICommandBarItemProps[] = [
        {
            key: 'filter',
            onRender: () => (
                <Stack role="menuitem" horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                    <LuisText variant="medium">{localize(messages.filter)}</LuisText>

                    <ComboBox
                        allowFreeform
                        scrollSelectedToTop
                        options={filterList}
                        useComboBoxAsMenuWidth
                        data-automation-id="filterBoxId"
                        selectedKey={currentFilter?.filterId}
                        calloutProps={{ calloutMaxHeight: 300 }}
                        ariaLabel={localize(messages.filterPlaceholder)}
                        placeholder={localize(messages.filterPlaceholder)}
                        onChange={(_, item, index) => onFilterChange(item, index)}
                    />

                    <IconButton
                        onClick={onRefresh}
                        data-automation-id="refreshIconId"
                        iconProps={{ iconName: 'Refresh', ariaLabel: 'Refresh' }}
                        ariaLabel={localize(messages.refreshAriaLabel)}
                    />
                </Stack>
            )
        }
    ];

    // Undefined ariaLabel to override the default one with the luisCommandBar
    return <LuisCommandBar items={items} ariaLabel={undefined} />;
};
